/**
 * Admin Console Component
 * User management, assessment overview, and admin functions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// Dialog replaced with custom modals for reliability
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Users, FileText, Settings, Plus, Trash2, RefreshCw, 
  Shield, ChevronLeft, UserCog, Download, Key, AlertCircle,
  CheckCircle, XCircle, Clock, BarChart3, X, Image as ImageIcon,
  Palette
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const MAX_LOGO_BYTES = 500 * 1024;

const AdminConsole = ({ onClose }) => {
  const { authFetch, user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // User form state
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'consultant' });
  const [addingUser, setAddingUser] = useState(false);
  
  // Password reset state
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
  const [tempPassword, setTempPassword] = useState(null);

  // Branding state
  const [branding, setBranding] = useState({ consultancy_name: '', consultancy_logo_base64: '', primary_color_hex: '' });
  const [savingBranding, setSavingBranding] = useState(false);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await authFetch('/api/branding');
      if (res.ok) {
        const data = await res.json();
        setBranding({
          consultancy_name: data.consultancy_name || '',
          consultancy_logo_base64: data.consultancy_logo_base64 || '',
          primary_color_hex: data.primary_color_hex || '',
        });
      }
    } catch (_e) { /* silent */ }
  }, [authFetch]);

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      const res = await authFetch('/api/admin/branding', {
        method: 'PUT',
        body: JSON.stringify(branding),
      });
      if (res.ok) {
        toast.success('Branding saved', { description: 'Cover pages will use the updated branding from now.' });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error('Failed to save branding', { description: err.detail || 'Server error' });
      }
    } catch (e) {
      toast.error('Failed to save branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, assessmentsRes, statsRes] = await Promise.all([
        authFetch('/api/admin/users'),
        authFetch('/api/admin/assessments'),
        authFetch('/api/admin/stats'),
      ]);
      
      if (usersRes.ok) setUsers(await usersRes.json());
      if (assessmentsRes.ok) setAssessments(await assessmentsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
    fetchBranding();
  }, [fetchData, fetchBranding]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    setError(null);
    
    try {
      const response = await authFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(newUser),
      });
      
      if (!response.ok) {
        let detail = 'Failed to add user';
        try {
          const data = await response.json();
          detail = data.detail || detail;
        } catch {
          if (response.status === 400) detail = 'Invalid user data or email already exists.';
          if (response.status === 403) detail = 'Permission denied.';
        }
        throw new Error(detail);
      }
      
      setShowAddUser(false);
      setNewUser({ email: '', name: '', password: '', role: 'consultant' });
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      const response = await authFetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !isActive }),
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const response = await authFetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        fetchData();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await authFetch(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
      });
      
      if (response.ok) {
        const data = await response.json();
        setTempPassword(data.temporary_password);
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExportAssessments = () => {
    const csvData = assessments.map(a => ({
      'Assessment ID': a.id,
      'Client': a.client_name,
      'Assessor': a.user_name,
      'Email': a.user_email,
      'Status': a.status,
      'Score': a.overall_score || 'N/A',
      'Audit Risk': a.audit_risk || 'N/A',
      'Created': new Date(a.created_at).toLocaleDateString(),
      'Updated': new Date(a.updated_at).toLocaleDateString(),
    }));
    
    const headers = Object.keys(csvData[0] || {});
    const csv = [
      headers.join(','),
      ...csvData.map(row => headers.map(h => `"${row[h]}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `defenciq-assessments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case 'LOW': return 'bg-green-100 text-green-700';
      case 'MODERATE': return 'bg-yellow-100 text-yellow-700';
      case 'HIGH': return 'bg-orange-100 text-orange-700';
      case 'CRITICAL': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'draft': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Workbench
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-600" />
              <h1 className="text-lg font-semibold">Admin Console</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-slate-100">
              <UserCog className="h-3 w-3 mr-1" />
              {user?.name}
            </Badge>
            <Button variant="outline" size="sm" onClick={async () => { setLoading(true); await fetchData(); setLoading(false); }} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Users</p>
                    <p className="text-2xl font-bold">{stats.users.total}</p>
                  </div>
                  <Users className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-xs text-slate-400 mt-2">{stats.users.active} active</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Assessments</p>
                    <p className="text-2xl font-bold">{stats.assessments.total}</p>
                  </div>
                  <FileText className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-xs text-slate-400 mt-2">{stats.assessments.completed} completed</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">In Progress</p>
                    <p className="text-2xl font-bold">{stats.assessments.in_progress}</p>
                  </div>
                  <Clock className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-xs text-slate-400 mt-2">Active assessments</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Completion Rate</p>
                    <p className="text-2xl font-bold">
                      {stats.assessments.total > 0 
                        ? Math.round((stats.assessments.completed / stats.assessments.total) * 100)
                        : 0}%
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-slate-300" />
                </div>
                <p className="text-xs text-slate-400 mt-2">Of all assessments</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="assessments" className="gap-2">
              <FileText className="h-4 w-4" />
              Assessments
            </TabsTrigger>
            <TabsTrigger value="branding" className="gap-2" data-testid="branding-tab">
              <Palette className="h-4 w-4" />
              Branding
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>Add, edit, or remove consultant accounts</CardDescription>
                </div>
                <Button className="gap-2" onClick={() => setShowAddUser(true)} data-testid="add-user-btn">
                  <Plus className="h-4 w-4" />
                  Add User
                </Button>
                {showAddUser && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black/80" onClick={() => setShowAddUser(false)} />
                    <div className="relative z-50 w-full max-w-lg border bg-background p-6 shadow-lg sm:rounded-lg">
                      <div className="flex flex-col space-y-1.5 text-left mb-4">
                        <h2 className="text-lg font-semibold leading-none tracking-tight">Add New User</h2>
                        <p className="text-sm text-muted-foreground">Create a new consultant or admin account</p>
                      </div>
                      <form onSubmit={handleAddUser} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="new-name">Full Name</Label>
                          <Input
                            id="new-name"
                            value={newUser.name}
                            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                            placeholder="John Smith"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="new-email">Email</Label>
                          <Input
                            id="new-email"
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            placeholder="john@company.com"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="new-password">Password</Label>
                          <Input
                            id="new-password"
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            placeholder="Minimum 6 characters"
                            minLength={6}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="new-role">Role</Label>
                          <Select
                            value={newUser.role}
                            onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="consultant">Consultant</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" disabled={addingUser}>
                            {addingUser ? 'Adding...' : 'Add User'}
                          </Button>
                        </div>
                      </form>
                      <button
                        onClick={() => setShowAddUser(false)}
                        className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {users.map((u) => (
                      <div
                        key={u.id}
                        className={cn(
                          "flex items-center justify-between p-4 rounded-xl border",
                          u.is_active ? "bg-white" : "bg-slate-50 opacity-60"
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold",
                            u.role === 'admin' ? 'bg-slate-800' : 'bg-slate-400'
                          )}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{u.name}</span>
                              <Badge variant="outline" className={cn(
                                "text-xs",
                                u.role === 'admin' ? 'bg-slate-900 text-white' : 'bg-slate-100'
                              )}>
                                {u.role}
                              </Badge>
                              {!u.is_active && (
                                <Badge variant="outline" className="text-xs bg-red-50 text-red-600">
                                  Disabled
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{u.email}</p>
                            <p className="text-xs text-slate-400">
                              Last login: {u.last_login 
                                ? new Date(u.last_login).toLocaleDateString() 
                                : 'Never'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {u.id !== user?.id && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setTempPassword(null);
                                  setResetPasswordDialog({ open: true, user: u });
                                }}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleUserStatus(u.id, u.is_active)}
                              >
                                {u.is_active ? (
                                  <XCircle className="h-4 w-4 text-red-500" />
                                ) : (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteUser(u.id)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {u.id === user?.id && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-600">
                              You
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reset Password Modal */}
          {resetPasswordDialog.open && resetPasswordDialog.user && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="fixed inset-0 bg-black/80" onClick={() => { setResetPasswordDialog({ open: false, user: null }); setTempPassword(null); }} />
              <div className="relative z-50 w-full max-w-md border bg-background p-6 shadow-lg sm:rounded-lg">
                <div className="flex flex-col space-y-1.5 text-left mb-4">
                  <h2 className="text-lg font-semibold leading-none tracking-tight">Reset Password</h2>
                  <p className="text-sm text-muted-foreground">Reset password for {resetPasswordDialog.user.name}?</p>
                </div>
                {tempPassword ? (
                  <div className="space-y-4">
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription>
                        Password reset successfully. Share this temporary password:
                      </AlertDescription>
                    </Alert>
                    <div className="p-4 bg-slate-100 rounded-lg font-mono text-center text-lg">
                      {tempPassword}
                    </div>
                    <p className="text-sm text-slate-500 text-center">
                      The user should change this password after logging in.
                    </p>
                    <div className="flex justify-end">
                      <Button onClick={() => { setResetPasswordDialog({ open: false, user: null }); setTempPassword(null); }}>
                        Done
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => { setResetPasswordDialog({ open: false, user: null }); setTempPassword(null); }}>
                      Cancel
                    </Button>
                    <Button onClick={() => handleResetPassword(resetPasswordDialog.user.id)}>
                      Reset Password
                    </Button>
                  </div>
                )}
                <button
                  onClick={() => { setResetPasswordDialog({ open: false, user: null }); setTempPassword(null); }}
                  className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Assessments Tab */}
          <TabsContent value="assessments">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>All Assessments</CardTitle>
                  <CardDescription>View assessments from all users</CardDescription>
                </div>
                <Button variant="outline" onClick={handleExportAssessments} className="gap-2">
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {assessments.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No assessments yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {assessments.map((a) => (
                        <div
                          key={a.id}
                          className="flex items-center justify-between p-4 rounded-xl border bg-white"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{a.client_name}</span>
                              <Badge className={getStatusBadgeColor(a.status)}>
                                {a.status.replace('_', ' ')}
                              </Badge>
                              {a.audit_risk && (
                                <Badge className={getRiskBadgeColor(a.audit_risk)}>
                                  {a.audit_risk} Risk
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">
                              Assessor: {a.user_name} ({a.user_email})
                            </p>
                            <p className="text-xs text-slate-400">
                              Created: {new Date(a.created_at).toLocaleDateString()} | 
                              Updated: {new Date(a.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            {a.overall_score !== null && (
                              <div className={cn(
                                "text-2xl font-bold",
                                a.overall_score >= 70 ? 'text-green-600' :
                                a.overall_score >= 50 ? 'text-yellow-600' :
                                'text-red-600'
                              )}>
                                {a.overall_score}%
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Consultancy Branding</CardTitle>
                <CardDescription>
                  This is the consultancy-wide default applied to every report cover page.
                  Each engagement can also upload its own client logo on the engagement form,
                  which will override the consultancy logo on that report's cover.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Consultancy Name */}
                <div>
                  <Label htmlFor="consultancy-name">Consultancy Name</Label>
                  <Input
                    id="consultancy-name"
                    value={branding.consultancy_name}
                    onChange={(e) => setBranding({ ...branding, consultancy_name: e.target.value })}
                    placeholder="e.g. DefencIQ Consulting"
                    className="mt-1.5"
                    data-testid="input-consultancy-name"
                  />
                  <p className="text-xs text-slate-500 mt-1">Shown in the cover header and page footers.</p>
                </div>

                {/* Consultancy Logo */}
                <div>
                  <Label>Consultancy Logo</Label>
                  <div className="mt-1.5 flex items-start gap-4">
                    {branding.consultancy_logo_base64 ? (
                      <div className="flex items-center gap-3">
                        <div
                          className="h-20 w-40 rounded-md bg-slate-900 flex items-center justify-center p-2 border border-slate-200"
                          data-testid="consultancy-logo-preview"
                        >
                          <img
                            src={branding.consultancy_logo_base64}
                            alt="Consultancy logo"
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          onClick={() => setBranding({ ...branding, consultancy_logo_base64: '' })}
                          className="text-rose-600"
                          data-testid="btn-clear-consultancy-logo"
                        >
                          <X className="h-4 w-4 mr-1" /> Remove
                        </Button>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > MAX_LOGO_BYTES) {
                            toast.error('Logo too large', { description: 'Use an image under 500KB' });
                            e.target.value = '';
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => setBranding({ ...branding, consultancy_logo_base64: reader.result });
                          reader.readAsDataURL(file);
                        }}
                        className="cursor-pointer"
                        data-testid="input-consultancy-logo"
                      />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Recommended: PNG with transparent background, under 500KB. SVG is not supported by the PDF engine.
                  </p>
                </div>

                {/* Primary Color (optional - reserved for future cover styling) */}
                <div>
                  <Label htmlFor="primary-color">Primary Brand Colour (optional)</Label>
                  <div className="flex items-center gap-3 mt-1.5">
                    <Input
                      id="primary-color"
                      type="color"
                      value={branding.primary_color_hex || '#1a1f36'}
                      onChange={(e) => setBranding({ ...branding, primary_color_hex: e.target.value })}
                      className="h-10 w-20 p-1"
                      data-testid="input-primary-color"
                    />
                    <Input
                      type="text"
                      value={branding.primary_color_hex}
                      onChange={(e) => setBranding({ ...branding, primary_color_hex: e.target.value })}
                      placeholder="#1a1f36"
                      className="max-w-[160px]"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Reserved for future cover-page accents.
                  </p>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={saveBranding} disabled={savingBranding} data-testid="btn-save-branding">
                    {savingBranding ? 'Saving...' : 'Save Branding'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminConsole;
